import { useState, useEffect } from "react";
import axios from "axios";
import CustomModal from "./CustomModal";
import './Component.css';
import Swal from 'sweetalert2';
import { useAuth } from "../../auth";
import { useAppSelector } from '../../../../redux/hooks';

const API_URL = process.env.REACT_APP_API_URL

// Definir los posibles valores de 'status' como un tipo
type StatusType = "UP" | "Unknown" | "DOWN";

// Tipo para las conexiones
type Connection = {
    salesRecord: string;
    activeAt: any;
    connectionId: any;
    cloud: any;
    account: any;
    id: string;
    name: string;
    status: StatusType;
    type: number;
    maintenance_mode: boolean;
    speed: string;
};

// Tipo para los puertos
type PortGroup = {
    activeAt: any;
    salesRecord: string;
    DC_location: string;
    id: string;
    name: string;
    status: StatusType;
    type: number;
    maintenance_mode: boolean;
    connections: Connection[];
};

// Servicio para obtener la conexión de la nube
export function getCloudConnection(tenantId: number, SubscriptionId?: number) {
    return axios.get(API_URL + `/v1/consoleconnect`, {
        params: {
            tenantId: tenantId,
            suscriptionId: SubscriptionId,
        },
    });
}

// Servicio para obtener el historial de un puerto específico
export function getHistory(
    portId: string,
    type: number,
    tenant_id?: number,
    month?: number,
    year?: number
) {
    return axios.get(API_URL + `/v1/consoleconnect/history`, {
        params: {
            port: portId,
            tenant_id: tenant_id,
            type,
            month: month || undefined,
            year: year || undefined,
        },
    });
}

export function toggleMaintenanceMode(portId: string, type: number) {
    return axios.put(API_URL + `/v1/consoleconnect/maintenance`, null, {
        params: {
            port: portId,
            type,
        },
    });
}

// ---- Ajustamos la función de formato de ancho de banda
function formatBandwidth(bandwidth: string): string {
    if (!bandwidth || typeof bandwidth !== 'string') {
        return "N/A";
    }
    const parts = bandwidth.trim().split(' ');
    if (parts.length < 2) {
        return bandwidth;
    }
    const [value, unit] = parts;
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
        return bandwidth;
    }
    const formattedValue = numericValue.toLocaleString('en-US');
    return `${formattedValue} ${unit}`;
}

// ---- Ajustamos la función de fecha
function formatDate(dateString: string): string {
    if (!dateString) {
        return 'N/A';
    }
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
    });
}

export default function Component() {
    const [portGroups, setPortGroups] = useState<PortGroup[]>([]);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedPortId, setSelectedPortId] = useState<string>('');
    const [selectedType, setSelectedType] = useState<number>(0);
    const [selectedPortName, setSelectedPortName] = useState<string>('');
    const [historyData, setHistoryData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { currentUser } = useAuth();

    const TenantId = useAppSelector((state) => state.TenantSlice.id);
    const SubscriptionId = useAppSelector((state) => state.SuscriptionSlice.id);
    const Cloud = useAppSelector((state) => state.CloudSlice.Type);
    const Region = useAppSelector((state) => state.RegionSlice.name);

    const toggleMaintenanceWithConfirmation = async (
        id: string,
        type: number,
        maintenanceMode: boolean
    ) => {
        const actionText = maintenanceMode ? 'disable' : 'enable';
        const result = await Swal.fire({
            title: `Confirm to ${actionText} Maintenance Mode`,
            text: maintenanceMode
                ? 'Disabling this mode will deactivate maintenance mode for this connection!'
                : 'Enabling this mode will activate maintenance mode for this connection!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            toggleMaintenanceAndRefresh(id, type);
        }
    };

    const openHistoryModal = async (
        portId: string,
        type: number,
        portName: string,
        month?: number,
        year?: number
    ) => {
        try {
            const response = await getHistory(portId, type, TenantId, month, year);
            const history = response.data.data.history || [];

            if (historyModalOpen === false && history.length === 0) {
                setHistoryData(history);
                Swal.fire({
                    icon: 'info',
                    title: 'No data',
                    text: 'No history available for this connection.',
                    confirmButtonText: 'Accept',
                });
                return;
            }

            setHistoryData(history);
            setSelectedPortId(portId);
            setSelectedType(type);
            setSelectedPortName(portName);
            setHistoryModalOpen(true);
        } catch (err) {
            console.error("Error al obtener el historial", err);
        }
    };

    const handleConsult = (
        portId: string,
        type: number,
        portName: string,
        month: string,
        year: string
    ) => {
        openHistoryModal(portId, type, portName ?? '', parseInt(month), parseInt(year));
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            
            const subscriptionForRequest =
                (SubscriptionId === 0 || SubscriptionId === -1)
                    ? -1
                    : SubscriptionId;

            const response = await getCloudConnection(TenantId, subscriptionForRequest);

            const data = response.data.data.map((item: any) => ({
                id: item.id,
                name: item.name,
                status: item.status as StatusType,
                type: item.type,
                DC_location: item.DC_location,
                activeAt: item.activeAt,
                salesRecord: item.salesRecord,
                maintenance_mode: item.maintenance_mode,
                connections: item.connections.map((conn: any) => ({
                    id: conn.id,
                    name: conn.name,
                    status: conn.status as StatusType,
                    type: conn.type,
                    cloud: conn.cloud,
                    salesRecord: conn.salesRecord,
                    activeAt: conn.activeAt,
                    account: conn.account,
                    connectionId: conn.connectionId,
                    maintenance_mode: conn.maintenance_mode,
                    speed: conn.speed,
                })),
            }));

            setPortGroups(data);
        } catch (err) {
            setError("Error loading data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [TenantId, SubscriptionId]);

    const toggleMaintenanceAndRefresh = async (id: string, type: number) => {
        try {
            const response = await toggleMaintenanceMode(id, type);
            if (response.status === 201) {
                Swal.fire({
                    icon: 'success',
                    title: 'Maintenance mode toggled',
                    text: 'The maintenance mode has been successfully toggled.',
                    confirmButtonText: 'Accept',
                });
                fetchData();
            }
        } catch (err) {
            console.error("Error al cambiar el modo de mantenimiento", err);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'There was an error toggling the maintenance mode.',
                confirmButtonText: 'Accept',
            });
        }
    };

    if (loading) {
        return <p>Loading data...</p>;
    }
    if (error) {
        return <p>{error}</p>;
    }

    const getStatusIcon = (status: StatusType) => {
        switch (status) {
            case "UP":
                return (
                    <i
                        className="b bi-check2-circle text-azul"
                        style={{ fontSize: '24px' }}
                    ></i>
                );
            case "DOWN":
                return (
                    <i
                        className="b bi-arrow-down-circle"
                        style={{ fontSize: '24px', color: '#FF4089' }}
                    ></i>
                );
            case "Unknown":
            default:
                return (
                    <i
                        className="b bi-exclamation-triangle text-warning"
                        style={{ fontSize: '24px' }}
                    ></i>
                );
        }
    };

    const getMaintenanceIcon = (maintenance_mode: boolean) => {
        return maintenance_mode ? (
            <i
                className="b bi-tools"
                style={{ fontSize: '24px', color: '#28a745' }}
            ></i>
        ) : (
            <i
                className="b bi-tools"
                style={{ fontSize: '24px', color: '#FF4089' }}
            ></i>
        );
    };

    const getCloudIcon = (cloudName: string) => {
        const cloudIcons: { [key: string]: string } = {
            AWS: "/media/iconos/AWS2.svg",
            OCI: "/media/iconos/Oracle2.svg",
            AZR: "/media/iconos/Azure2.svg",
            GCP: "/media/iconos/google.png",
        };
        return cloudIcons[cloudName] ? (
            <img src={cloudIcons[cloudName]} alt={cloudName} className="cloud-icon" />
        ) : (
            cloudName
        );
    };

    const getFormattedType = (cloudName: string) => {
        const typeMappings: { [key: string]: string } = {
            AWS: "Direct Connect",
            OCI: "FastConnect",
            AZR: "ExpressRoute",
            GCP: "Partner Interconnect",
        };
        return typeMappings[cloudName] || "Unknown";
    };

    // Función para renderizar `account` y `connectionId` sin tronar
    // (si son objetos, los volvemos string o ponemos "N/A")
    const renderValue = (val: any) => {
        if (typeof val === 'string') {
            return val;
        }
        if (val && typeof val === 'object' && Object.keys(val).length > 0) {
            return JSON.stringify(val);
        }
        return "N/A";
    };

    return (
        <div className="component-container">
            {portGroups.length === 0 ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        minHeight: '200px',
                        width: '100%',
                    }}
                >
                    <p style={{ textAlign: 'center', color: 'gray', fontSize: '18px' }}>
                        No Data Available
                    </p>
                </div>
            ) : (
                portGroups.map((group) => (
                    <div key={group.id} className="connection-box">
                        <div className="connection-header">
                            <div className="connection-grid">
                                <strong className="text-azul f-titulo">Physical port</strong>
                            </div>
                            <span className="location-text">{group.DC_location}</span>
                            <div className="text-negro">
                                <span style={{ fontSize: '16px' }}>{group.name}</span>
                                <span className="tooltip-connectivity" key={group.id}>
                                    !
                                    <div className="tooltip-content">
                                        Sales Record: {group.salesRecord}
                                        <br />
                                        Active Since: {formatDate(group.activeAt)}
                                        <br />
                                    </div>
                                </span>
                            </div>
                            <div className={`connection-status`}>
                                {getStatusIcon(group.status)}
                                {currentUser?.ListPermiso?.includes('Tenant.list') ? (
                                    <button
                                        className="view-mode-button m-left-1"
                                        onClick={() =>
                                            toggleMaintenanceWithConfirmation(
                                                group.id,
                                                group.type,
                                                group.maintenance_mode
                                            )
                                        }
                                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                    >
                                        {getMaintenanceIcon(group.maintenance_mode)}
                                    </button>
                                ) : (
                                    <span className="m-left-1">
                                        {getMaintenanceIcon(group.maintenance_mode)}
                                    </span>
                                )}
                            </div>
                            <button
                                className="view-history-button"
                                onClick={() => openHistoryModal(group.id, group.type, group.name)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                            >
                                <i
                                    className="b bi-clock-history"
                                    style={{ fontSize: '24px', color: '#02C8C1' }}
                                ></i>
                            </button>
                        </div>

                        <div className="connection-grid">
                            <strong className="text-azul">Cloud Connections</strong>
                            <strong className="text-primary">Cloud</strong>
                            <strong className="text-primary">Type</strong>
                            <strong className="text-primary">Speed</strong>
                            <strong className="text-primary">Status</strong>
                            <strong className="text-primary">Maintenance mode</strong>
                            <strong className="text-primary">History</strong>
                        </div>

                        <ul className="connection-list">
                            {group.connections.length === 0 ? (
                                <p style={{ textAlign: 'center', color: 'gray', padding: '10px' }}>
                                    No Connections Available
                                </p>
                            ) : (
                                group.connections.map((connection) => (
                                    <li key={connection.id}>
                                        <div className="text-negro">
                                            {connection.name}
                                            {/* No usar `${connection.account}-${connection.connectionId}` como key 
                          porque son objetos a veces. Simplemente key={connection.id}. */}
                                            <span className="tooltip-connectivity" key={connection.id + "_tt"}>
                                                !
                                                <div className="tooltip-content">
                                                    Account: {renderValue(connection.account)}
                                                    <br />
                                                    Connection ID: {renderValue(connection.connectionId)}
                                                    <br />
                                                    Sales Record: {connection.salesRecord}
                                                    <br />
                                                    Active Since: {formatDate(connection.activeAt)}
                                                    <br />
                                                </div>
                                            </span>
                                        </div>
                                        <div className={`connection-status`}>
                                            {getCloudIcon(connection.cloud)}
                                        </div>
                                        <div className={`connection-status text-negro`}>
                                            {getFormattedType(connection.cloud)}
                                        </div>
                                        <div className={`connection-status text-negro`}>
                                            {formatBandwidth(connection.speed)}
                                        </div>
                                        <div className={`connection-status`}>
                                            {getStatusIcon(connection.status)}
                                        </div>
                                        <div className={`connection-status`}>
                                            {currentUser?.ListPermiso?.includes('Tenant.list') ? (
                                                <button
                                                    className="view-mode-button"
                                                    onClick={() =>
                                                        toggleMaintenanceWithConfirmation(
                                                            connection.id,
                                                            connection.type,
                                                            connection.maintenance_mode
                                                        )
                                                    }
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                >
                                                    {getMaintenanceIcon(connection.maintenance_mode)}
                                                </button>
                                            ) : (
                                                getMaintenanceIcon(connection.maintenance_mode)
                                            )}
                                        </div>
                                        <div>
                                            <button
                                                className="view-history-button"
                                                onClick={() =>
                                                    openHistoryModal(connection.id, connection.type, connection.name)
                                                }
                                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                            >
                                                <i
                                                    className="b bi-clock-history"
                                                    style={{ fontSize: '24px', color: '#02C8C1' }}
                                                ></i>
                                            </button>
                                        </div>
                                    </li>
                                ))
                            )}
                        </ul>
                    </div>
                ))
            )}

            <CustomModal
                show={historyModalOpen}
                handleClose={() => setHistoryModalOpen(false)}
                handleConsult={handleConsult}
                title="Connection History"
                portName={selectedPortName}
                historyData={historyData}
                portId={selectedPortId}
                type={selectedType}
            />
        </div>
    );
}
